import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Edit, Trash2, Play, Square, X, ArrowLeft, MapPin, Clock, Car, User, DollarSign, Route, Info
} from 'lucide-react';
import { viagensService } from '../../services/viagens';
import ChecklistDaViagem from './ChecklistDaViagem';
import DesvioDaRota from '../../components/DesvioDaRota';
import { useConfirm, usePrompt } from '../../components/ConfirmDialog';
import { formatCurrency, formatDate, formatKilometers } from '../../utils/formatters';

const ViagemDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const showPrompt = usePrompt();

  const { data, isLoading, error } = useQuery({
    queryKey: ['viagem', id],
    queryFn: () => viagensService.getViagem(Number(id)),
    enabled: Boolean(id)
  });

  const deleteMutation = useMutation({
    mutationFn: () => viagensService.deleteViagem(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagens'] });
      toast.success('Viagem excluída com sucesso!');
      navigate('/viagens');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir viagem');
    }
  });

  const iniciarMutation = useMutation({
    mutationFn: () => viagensService.iniciarViagem(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagem', id] });
      toast.success('Viagem iniciada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar viagem');
    }
  });

  const finalizarMutation = useMutation({
    mutationFn: (kmChegada: number) => viagensService.finalizarViagem(Number(id), { km_chegada: kmChegada }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagem', id] });
      toast.success('Viagem finalizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao finalizar viagem');
    }
  });

  const cancelarMutation = useMutation({
    mutationFn: () => viagensService.cancelarViagem(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viagem', id] });
      toast.success('Viagem cancelada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao cancelar viagem');
    }
  });

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Excluir viagem',
      message: 'Tem certeza que deseja excluir esta viagem? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      variant: 'danger',
    });
    if (ok) {
      deleteMutation.mutate();
    }
  };

  const handleIniciar = async () => {
    const ok = await confirm({
      title: 'Iniciar viagem',
      message: 'Deseja iniciar esta viagem?',
      confirmLabel: 'Iniciar',
      variant: 'primary',
    });
    if (ok) {
      iniciarMutation.mutate();
    }
  };

  const handleFinalizar = async () => {
    const kmChegada = await showPrompt({
      title: 'Finalizar viagem',
      message: `Informe a quilometragem do painel na chegada. `
        + `Ela não pode ser menor que a de saída (${formatKilometers(viagem?.km_saida ?? 0)}). `
        + `A data e a hora da chegada são registradas agora, automaticamente.`,
      confirmLabel: 'Finalizar',
      variant: 'primary',
      input: {
        type: 'number',
        label: 'Km de chegada',
        placeholder: `Maior que ${Math.round(Number(viagem?.km_saida ?? 0))}`,
        // O mínimo é o km de saída, e não zero: o campo passa a recusar na
        // própria tela o que o servidor recusaria depois.
        min: Number(viagem?.km_saida ?? 0),
        required: true,
      },
    });
    if (kmChegada && !isNaN(Number(kmChegada))) {
      finalizarMutation.mutate(Number(kmChegada));
    }
  };

  const handleCancelar = async () => {
    const ok = await confirm({
      title: 'Cancelar viagem',
      message: 'Deseja cancelar esta viagem?',
      confirmLabel: 'Cancelar viagem',
      cancelLabel: 'Voltar',
      variant: 'danger',
    });
    if (ok) {
      cancelarMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">Erro ao carregar viagem</p>
        <button 
          onClick={() => navigate('/viagens')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Voltar para lista
        </button>
      </div>
    );
  }

  const viagem = data.data;

  const getStatusBadge = (status: string) => {
    const variants = {
      concluida: 'bg-green-100 text-green-800',
      em_andamento: 'bg-blue-100 text-blue-800',
      planejada: 'bg-yellow-100 text-yellow-800',
      cancelada: 'bg-red-100 text-red-800'
    } as const;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getTipoBadge = (tipo: string) => {
    const colors = {
      comercial: 'bg-blue-100 text-blue-800',
      particular: 'bg-green-100 text-green-800',
      manutencao: 'bg-orange-100 text-orange-800',
      emergencia: 'bg-red-100 text-red-800',
      outros: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[tipo as keyof typeof colors] || colors.outros}`}>
        {tipo.toUpperCase()}
      </span>
    );
  };

  const kmRodado: number | null =
    viagem?.km_chegada != null && viagem?.km_saida != null
      ? Number(viagem.km_chegada) - Number(viagem.km_saida)
      : viagem?.km_percorridos != null
        ? Number(viagem.km_percorridos)
        : null;

  const calcularDuracao = () => {
    if (viagem.data_chegada && viagem.data_saida) {
      const inicio = new Date(viagem.data_saida);
      const fim = new Date(viagem.data_chegada);
      const diffMs = fim.getTime() - inicio.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHours}h ${diffMinutes}min`;
    }
    return null;
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/viagens')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para lista
        </button>
        
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{viagem.finalidade}</h1>
            <p className="text-gray-600">
              {[viagem.origem, ...(viagem.paradas ?? []).map((p) => p.endereco), viagem.destino].join(' → ')}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate(`/viagens/${id}/editar`)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Edit className="w-4 h-4 mr-1" />
              Editar
            </button>
            {viagem.status === 'planejada' && (
              <button
                onClick={handleIniciar}
                disabled={iniciarMutation.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <Play className="w-4 h-4 mr-1" />
                Iniciar
              </button>
            )}
            {viagem.status === 'em_andamento' && (
              <button
                onClick={handleFinalizar}
                disabled={finalizarMutation.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <Square className="w-4 h-4 mr-1" />
                Finalizar
              </button>
            )}
            {(viagem.status === 'planejada' || viagem.status === 'em_andamento') && (
              <button
                onClick={handleCancelar}
                disabled={cancelarMutation.isPending}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Excluir
            </button>
          </div>
        </div>

        {/* Orientacao do fluxo.
            A duvida "por que nao consigo finalizar?" nasce de nao saber que ha
            um passo antes: viagem planejada precisa ser INICIADA. O texto muda
            conforme o estado, para dizer o que fazer agora, e nao descrever o
            processo inteiro. */}
        {viagem.status === 'planejada' && (
          <div className="mt-4 flex gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-sky-500" />
            <span>
              Esta viagem ainda <b>não começou</b>. Clique em <b>Iniciar</b> quando o veículo sair — só
              depois disso é possível finalizar.
            </span>
          </div>
        )}

        {viagem.status === 'em_andamento' && (
          <div className="mt-4 flex gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-sky-500" />
            <span>
              Ao voltar, clique em <b>Finalizar</b> e informe a <b>quilometragem do painel</b>
              {viagem.km_saida != null ? (
                <> — ela precisa ser maior ou igual à de saída ({formatKilometers(viagem.km_saida)})</>
              ) : (
                // Viagem iniciada sem km de saida: o proprio finalizar pede os
                // dois numeros, e prometer "maior que a de saida" sem ter uma
                // deixaria o gestor procurando um valor que nao existe.
                <> — como esta viagem não tem quilometragem de saída, informe as duas</>
              )}
              . A data e a hora são registradas no momento do clique, então{' '}
              <b>ida e volta no mesmo dia é normal</b>.
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Principais */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Informações da Viagem</h2>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  {getStatusBadge(viagem.status)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Tipo</dt>
                <dd className="mt-1">
                  {getTipoBadge(viagem.tipo)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Data de Saída</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(viagem.data_saida)}
                </dd>
              </div>
              {viagem.data_chegada && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Data de Chegada</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(viagem.data_chegada)}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">KM de Saída</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {/* Quem informa e o motorista, ao sair. Ate la o campo esta
                      vazio de proposito, e dizer isso evita que o gestor ache
                      que o dado se perdeu. */}
                  {viagem.km_saida != null
                    ? formatKilometers(viagem.km_saida)
                    : 'Aguardando o motorista informar'}
                </dd>
              </div>
              {viagem.km_chegada != null && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">KM de Chegada</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatKilometers(viagem.km_chegada)}
                  </dd>
                </div>
              )}
              {kmRodado != null && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">KM Percorridos</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {formatKilometers(kmRodado)}
                  </dd>
                </div>
              )}
              {calcularDuracao() && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Duração</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {calcularDuracao()}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Rota */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Route className="w-5 h-5 mr-2" />
              Rota
            </h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 text-green-600 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Origem</div>
                  <div className="text-sm text-gray-500">{viagem.origem}</div>
                </div>
              </div>
              {(viagem.paradas ?? []).map((parada, indice) => (
                <div key={indice} className="flex items-center">
                  <MapPin className="w-4 h-4 text-amber-500 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Parada {indice + 1}</div>
                    <div className="text-sm text-gray-500">{parada.endereco}</div>
                  </div>
                </div>
              ))}
              <div className="flex items-center">
                <MapPin className="w-4 h-4 text-red-600 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {viagem.paradas?.length ? 'Destino final' : 'Destino'}
                  </div>
                  <div className="text-sm text-gray-500">{viagem.destino}</div>
                </div>
              </div>
              {viagem.rota && (
                <div className="flex items-center">
                  <Route className="w-4 h-4 text-blue-600 mr-3" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Rota</div>
                    <div className="text-sm text-gray-500">{viagem.rota}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custos */}
          {(viagem.valor_combustivel || viagem.valor_pedagio || viagem.valor_outros || viagem.valor_total) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DollarSign className="w-5 h-5 mr-2" />
                Custos
              </h2>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                {viagem.valor_combustivel && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Combustível</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatCurrency(viagem.valor_combustivel)}
                    </dd>
                  </div>
                )}
                {viagem.valor_pedagio && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Pedágio</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatCurrency(viagem.valor_pedagio)}
                    </dd>
                  </div>
                )}
                {viagem.valor_outros && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Outros</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatCurrency(viagem.valor_outros)}
                    </dd>
                  </div>
                )}
                {viagem.valor_total && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-semibold">
                      {formatCurrency(viagem.valor_total)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Descrição e Observações */}
          {(viagem.descricao || viagem.observacoes) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Detalhes</h2>
              {viagem.descricao && (
                <div className="mb-4">
                  <dt className="text-sm font-medium text-gray-500">Descrição</dt>
                  <dd className="mt-1 text-sm text-gray-900">{viagem.descricao}</dd>
                </div>
              )}
              {viagem.observacoes && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Observações</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{viagem.observacoes}</dd>
                </div>
              )}
            </div>
          )}

          {/* Rota prevista contra hodômetro. Só a gestão vê: o servidor não
              manda este campo para o portal do motorista. */}
          {(viagem.desvio_da_rota || viagem.distancia_prevista_km != null) && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Rota prevista x rodado</h2>
              <DesvioDaRota
                desvio={viagem.desvio_da_rota}
                previstoKm={viagem.distancia_prevista_km != null ? Number(viagem.distancia_prevista_km) : null}
                rodadoKm={viagem.km_percorridos != null ? Number(viagem.km_percorridos) : null}
              />
            </div>
          )}

          {/* A conferência do veículo feita pelo motorista antes de sair.
              Coletar 25 respostas que ninguém lê é pior do que não coletar. */}
          <ChecklistDaViagem viagemId={viagem.id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Veículo */}
          {viagem.veiculo && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Car className="w-5 h-5 mr-2" />
                Veículo
              </h2>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Placa</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {viagem.veiculo.placa}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Modelo</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {viagem.veiculo.marca} {viagem.veiculo.modelo}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Ano</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {viagem.veiculo.ano_fabricacao}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Quilometragem Atual</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatKilometers(viagem.veiculo.quilometragem)}
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Motorista */}
          {viagem.motorista && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Motorista
              </h2>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Nome</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {viagem.motorista.nome}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">CPF</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {viagem.motorista.cpf}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">CNH</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {viagem.motorista.cnh} - {viagem.motorista.categoria_cnh}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      viagem.motorista.status === 'ativo' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {viagem.motorista.status.toUpperCase()}
                    </span>
                  </dd>
                </div>
              </div>
            </div>
          )}

          {/* Informações Adicionais */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Informações
            </h2>
            <div className="space-y-3">
              {viagem.numero_passageiros && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Passageiros</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {viagem.numero_passageiros}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Criado em</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(viagem.created_at)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Atualizado em</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(viagem.updated_at)}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViagemDetail;
